import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AsaAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "asa_token";

function setAuthHeader(token) {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
}

export function AsaAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setReady(true); return; }
    setAuthHeader(token);
    axios.get(`${API}/auth/me`)
      .then((res) => setUser(res.data.user))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthHeader(null); })
      .finally(() => setReady(true));
  }, []);

  // Auto-logout when a token expires or is rejected mid-session (401),
  // except on the login/register calls themselves (they surface their own errors).
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const isAuthCall = url.includes("/auth/login") || url.includes("/auth/register");
        if (status === 401 && !isAuthCall && localStorage.getItem(TOKEN_KEY)) {
          localStorage.removeItem(TOKEN_KEY);
          setAuthHeader(null);
          setUser(null);
          if (!window.location.pathname.startsWith("/asa/login")) {
            window.location.href = "/asa/login";
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const persistToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setAuthHeader(token);
  };

  // Create account (pending until payment confirmed). Returns user.
  const register = async ({ name, email, password, tier, billing, consent, diagnosed, diagnosisConditions, diagnosisWhen, symptomsNote, medications, recentTests, recentTestsDetail }) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      name, email, password, tier, billing, consent,
      diagnosed, diagnosis_conditions: diagnosisConditions, diagnosis_when: diagnosisWhen, symptoms_note: symptomsNote,
      medications, recent_tests: recentTests, recent_tests_detail: recentTestsDetail,
    });
    persistToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async ({ email, password }) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persistToken(data.token);
    setUser(data.user);
    return data.user;
  };

  // Re-fetch the current user (e.g. after a payment activates/upgrades the subscription).
  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
      return data.user;
    } catch (e) { return null; }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setUser(null);
  };

  return (
    <AsaAuthContext.Provider value={{ user, ready, register, login, refreshUser, logout }}>
      {children}
    </AsaAuthContext.Provider>
  );
}

export const useAsaAuth = () => useContext(AsaAuthContext);
